import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, HttpError } from '../api';
import { errorText, t } from '../i18n';
import { tPart } from '../bidi-html';
import { go } from '../router';
import { friendAskCard, noteNewFriend } from './friend-bits';
import { nameOf } from './raid/raid-bits';

/**
 * A friend link (`/friend/<secret>`, direct friends ticket 03): "name wants to be friends" with what each of you
 * will see, one tap to agree. Opening it only looks the link up; nothing is made until the learner agrees, and the
 * server answers a lookup with the maker's username and nothing else. Signed out, the lookup answers 401 and
 * `api.ts` sends the learner to sign-in, which brings them back here (`return-to.ts`). A refusal always leaves a
 * way to the Friends screen; the secret is only ever in the path and the request body, never shown or logged.
 * Personal mode has no friends: this screen sends the learner home.
 */
@customElement('friend-link-view')
export class FriendLinkView extends LitElement {
  @property() token = '';
  @state() private loading = true;
  @state() private name = '';
  @state() private already = false;
  @state() private busy = false;
  @state() private error = '';
  /** The error is a real refusal (a 4xx with a reason): the link is done with. Otherwise the learner can try again. */
  private final = true;

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    void this.look();
  }

  /** A refusal from the server (4xx) is final; a network failure or a 5xx is not. */
  private static isFinal(e: unknown): boolean {
    return e instanceof HttpError && e.status >= 400 && e.status < 500;
  }

  private async look() {
    this.loading = true;
    this.error = '';
    try {
      const r = await api.lookupFriendCode(this.token);
      // A circle's code in a friend link's place is no link: the same refusal as any other wrong secret.
      if (r.kind !== 'friend') {
        this.final = true;
        this.error = t('error.friend-offer-invalid');
        return;
      }
      this.name = r.name;
      this.already = !!r.already;
    } catch (e) {
      // The route is absent (404 `not-found`) only in personal mode; a refused link is 404 `friend-offer-invalid`.
      if (e instanceof HttpError && e.status === 404 && e.code === 'not-found') {
        go('/', true);
        return;
      }
      this.final = FriendLinkView.isFinal(e);
      this.error = errorText(e);
    } finally {
      this.loading = false;
    }
  }

  private agree = async () => {
    if (this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      const r = await api.acceptFriendLink(this.token);
      if (!this.isConnected) return; // the learner left meanwhile: nothing here may move them
      // Back must not return to the link; Friends says it is done.
      if (!r.already) noteNewFriend(r.friend.name);
      go('/circle', true);
    } catch (e) {
      this.final = FriendLinkView.isFinal(e);
      this.error = errorText(e);
    } finally {
      this.busy = false;
    }
  };

  private who() {
    return nameOf(this.name);
  }

  render() {
    return html`
      <div class="screen circle-screen">
        <div class="topbar"><h1 style="font-size:17px">${t('circle.title')}</h1></div>
        ${this.loading
          ? html`<p class="muted">${t('circle.loading')}</p>`
          : this.error && (this.final || !this.name)
            ? html`<p class="circle-error" role="alert">${this.error}</p>
                ${this.final ? nothing : html`<button class="btn primary block friend-retry" @click=${() => this.look()}>${t('friends.tryAgain')}</button>`}
                ${this.toFriends()}`
            : this.already
              ? html`<div class="card friend-already">
                    <p>${tPart('friends.already', 'name', this.who())}</p>
                  </div>${this.toFriends()}`
              : html`${this.error ? html`<p class="circle-error" role="alert">${this.error}</p>` : nothing}${this.renderAsk()}`}
      </div>
    `;
  }

  private toFriends() {
    return html`<button class="btn primary block friend-to-friends" @click=${() => go('/circle', true)}>${t('friends.toFriends')}</button>`;
  }

  private renderAsk() {
    return friendAskCard({ name: this.name, busy: this.busy, onAgree: this.agree, onCancel: () => go('/', true) });
  }
}
